import type {
  Category,
  CategoryTree,
  CreateCategoryInput,
  UpdateCategoryInput,
} from '@/lib/types/category.types';
import apiClient from './client';
import { extractArrayResponse, extractResponseData, handleApiError } from './responseHelpers';

// Helper to transform backend category (snake_case) to frontend Category (camelCase)
function transformBackendCategory(backendCategory: any): Category {
  const transformed = {
    id: String(backendCategory.id || ''),
    nameAr: backendCategory.name_ar || backendCategory.nameAr || '',
    nameEn: backendCategory.name_en || backendCategory.nameEn || '',
    parentId: backendCategory.parent_id || backendCategory.parentId || null,
    icon: backendCategory.icon || '',
    description: backendCategory.description || '',
    isActive: backendCategory.is_active ?? backendCategory.isActive ?? true,
    sortOrder: backendCategory.sort_order || backendCategory.sortOrder || 0,
    createdAt: backendCategory.created_at || backendCategory.createdAt || new Date().toISOString(),
    updatedAt: backendCategory.updated_at || backendCategory.updatedAt || new Date().toISOString(),
    suppliersCount: backendCategory.suppliers_count || backendCategory.suppliersCount || 0,
    requestsCount: backendCategory.requests_count || backendCategory.requestsCount || 0,
    slug: backendCategory.slug || '',
    parent: backendCategory.parent ? transformBackendCategory(backendCategory.parent) : undefined,
    children: backendCategory.children
      ? backendCategory.children.map((child: any) => transformBackendCategory(child))
      : undefined,
  };

  // Debug: Log transformation for first category
  if (process.env.NODE_ENV === 'development' && !transformed.id.includes('logged')) {
    console.log('🔄 transformBackendCategory:', {
      input: {
        id: backendCategory.id,
        name_ar: backendCategory.name_ar,
        name_en: backendCategory.name_en,
        slug: backendCategory.slug,
      },
      output: {
        id: transformed.id,
        nameAr: transformed.nameAr,
        nameEn: transformed.nameEn,
        slug: transformed.slug,
      },
    });
    // Mark as logged to avoid spam
    (transformed as any)._logged = true;
  }

  return transformed;
}

export const categoriesApi = {
  getAll: async (): Promise<Category[]> => {
    try {
      const response = await apiClient.get<{ data: any[] } | Category[]>('/v1/categories');

      // Debug: Log the raw response structure
      if (process.env.NODE_ENV === 'development') {
        console.log('📥 Categories API Raw Response:', {
          hasData: !!response.data,
          dataType: typeof response.data,
          isArray: Array.isArray(response.data),
          hasNestedData: !!(
            response.data &&
            typeof response.data === 'object' &&
            'data' in response.data
          ),
          nestedDataType:
            response.data && typeof response.data === 'object' && 'data' in response.data
              ? typeof (response.data as { data: any[] }).data
              : 'undefined',
          nestedDataIsArray:
            response.data && typeof response.data === 'object' && 'data' in response.data
              ? Array.isArray((response.data as { data: any[] }).data)
              : false,
          responseData: response.data,
          responseDataKeys:
            response.data && typeof response.data === 'object' ? Object.keys(response.data) : [],
        });
      }

      const categories = extractArrayResponse<any>(response);

      // Debug: Log extracted categories
      if (process.env.NODE_ENV === 'development') {
        console.log('📦 Extracted Categories Array:', {
          count: categories?.length || 0,
          isArray: Array.isArray(categories),
          firstCategory: categories?.[0],
          allCategories: categories,
        });
      }

      // Ensure categories is an array
      if (!Array.isArray(categories)) {
        console.error('❌ Categories API: Expected array but got:', {
          type: typeof categories,
          value: categories,
          response: response.data,
        });
        return [];
      }

      const transformed = categories.map(transformBackendCategory);

      // Debug: Log transformed categories
      if (process.env.NODE_ENV === 'development') {
        console.log('✨ Transformed Categories:', {
          count: transformed.length,
          firstCategory: transformed[0],
          sampleNames: transformed
            .slice(0, 3)
            .map((c) => ({ nameAr: c.nameAr, nameEn: c.nameEn, slug: c.slug })),
        });
      }

      return transformed;
    } catch (error) {
      // Avoid noisy logs during build when backend isn't running (e.g. sitemap)
      const isBuildTime = typeof window === 'undefined';
      const isConnectionRefused =
        (error as any)?.code === 'ECONNREFUSED' ||
        (error as Error)?.message?.includes('ECONNREFUSED');
      if (!(isBuildTime && isConnectionRefused)) {
        console.error('❌ Categories API Error:', error);
      }
      // Return empty array instead of throwing to prevent map errors
      // The error will be handled by the hook's error state
      return [];
    }
  },

  getById: async (id: string): Promise<Category> => {
    try {
      const response = await apiClient.get<{ data: any } | Category>(`/v1/categories/${id}`);
      const categoryData = extractResponseData<any>(response);
      return transformBackendCategory(categoryData);
    } catch (error) {
      handleApiError(error);
    }
  },

  create: async (categoryData: CreateCategoryInput): Promise<Category> => {
    try {
      // Transform camelCase to snake_case for backend
      // Ensure parent_id is either a valid UUID string or null (not empty string)
      const parentId =
        categoryData.parentId && categoryData.parentId.trim() !== ''
          ? categoryData.parentId.trim()
          : null;

      const backendData = {
        name_ar: categoryData.nameAr?.trim() || '',
        name_en: categoryData.nameEn?.trim() || '',
        parent_id: parentId,
        icon: categoryData.icon?.trim() || '',
        description: categoryData.description?.trim() || '',
        is_active: categoryData.isActive ?? true,
        sort_order: categoryData.sortOrder ?? 0,
      };

      const response = await apiClient.post<{ message: string; data: any }>(
        '/v1/categories',
        backendData
      );
      const responseCategoryData = extractResponseData<any>(response);
      return transformBackendCategory(responseCategoryData);
    } catch (error) {
      handleApiError(error);
    }
  },

  update: async (id: string, categoryData: UpdateCategoryInput): Promise<Category> => {
    try {
      // Transform camelCase to snake_case for backend
      const backendData: Record<string, any> = {};
      if (categoryData.nameAr !== undefined) backendData.name_ar = categoryData.nameAr;
      if (categoryData.nameEn !== undefined) backendData.name_en = categoryData.nameEn;
      if (categoryData.parentId !== undefined) backendData.parent_id = categoryData.parentId;
      if (categoryData.icon !== undefined) backendData.icon = categoryData.icon;
      if (categoryData.description !== undefined)
        backendData.description = categoryData.description;
      if (categoryData.isActive !== undefined) backendData.is_active = categoryData.isActive;
      if (categoryData.sortOrder !== undefined) backendData.sort_order = categoryData.sortOrder;

      const response = await apiClient.put<{ message: string; data: any }>(
        `/v1/categories/${id}`,
        backendData
      );
      const categoryDataResponse = extractResponseData<any>(response);
      return transformBackendCategory(categoryDataResponse);
    } catch (error) {
      handleApiError(error);
    }
  },

  getTree: async (): Promise<CategoryTree[]> => {
    try {
      const response = await apiClient.get<{ data: any[] } | CategoryTree[]>('/v1/categories/tree');
      const treeData = extractArrayResponse<any>(response);
      // Transform tree data if needed
      // Ensure treeData is an array before mapping
      if (!Array.isArray(treeData)) {
        console.error('❌ getTree: Expected array but got:', {
          type: typeof treeData,
          value: treeData,
        });
        return [];
      }
      return treeData.map((item: any) => ({
        ...transformBackendCategory(item.category || item),
        subcategories: item.children || item.subcategories || [],
      })) as CategoryTree[];
    } catch (error) {
      console.error('❌ getTree Error:', error);
      // Return empty array instead of throwing to prevent map errors
      return [];
    }
  },

  getActive: async (): Promise<Category[]> => {
    try {
      const response = await apiClient.get<{ data: any[] } | Category[]>('/v1/categories/active');
      const categories = extractArrayResponse<any>(response);
      // Ensure categories is an array before mapping
      if (!Array.isArray(categories)) {
        console.error('❌ getActive: Expected array but got:', {
          type: typeof categories,
          value: categories,
        });
        return [];
      }
      return categories.map(transformBackendCategory);
    } catch (error) {
      console.error('❌ getActive Error:', error);
      // Return empty array instead of throwing to prevent map errors
      return [];
    }
  },

  getSubcategories: async (id: string): Promise<Category[]> => {
    try {
      const response = await apiClient.get<{ data: any[] } | Category[]>(
        `/v1/categories/${id}/subcategories`
      );
      const categories = extractArrayResponse<any>(response);
      // Ensure categories is an array before mapping
      if (!Array.isArray(categories)) {
        console.error('❌ getSubcategories: Expected array but got:', {
          type: typeof categories,
          value: categories,
        });
        return [];
      }
      return categories.map(transformBackendCategory);
    } catch (error) {
      console.error('❌ getSubcategories Error:', error);
      // Return empty array instead of throwing to prevent map errors
      return [];
    }
  },

  getParent: async (): Promise<Category[]> => {
    try {
      const response = await apiClient.get<{ data: any[] }>('/v1/categories/parent');
      const categories = extractArrayResponse<any>(response);
      // Ensure categories is an array before mapping
      if (!Array.isArray(categories)) {
        console.error('❌ getParent: Expected array but got:', {
          type: typeof categories,
          value: categories,
        });
        return [];
      }
      return categories.map(transformBackendCategory);
    } catch (error) {
      console.error('❌ getParent Error:', error);
      // Return empty array instead of throwing to prevent map errors
      return [];
    }
  },

  delete: async (id: string): Promise<void> => {
    try {
      await apiClient.delete(`/v1/categories/${id}`);
    } catch (error) {
      throw error;
    }
  },

  activate: async (id: string): Promise<Category> => {
    try {
      const response = await apiClient.patch<{ data: any } | Category>(
        `/v1/categories/${id}/activate`
      );
      const categoryData = extractResponseData<any>(response);
      return transformBackendCategory(categoryData);
    } catch (error) {
      handleApiError(error);
    }
  },

  deactivate: async (id: string): Promise<Category> => {
    try {
      const response = await apiClient.patch<{ data: any } | Category>(
        `/v1/categories/${id}/deactivate`
      );
      const categoryData = extractResponseData<any>(response);
      return transformBackendCategory(categoryData);
    } catch (error) {
      handleApiError(error);
    }
  },

  reorder: async (categoryIds: string[]): Promise<Category[]> => {
    try {
      const response = await apiClient.put<{ data: any[] } | Category[]>('/v1/categories/reorder', {
        categoryIds,
      });
      const categories = extractArrayResponse<any>(response);

      // ✅ Defensive check: ensure categories is always an array
      if (!Array.isArray(categories)) {
        console.warn(
          '⚠️ categoriesApi.reorder: categories is not an array, returning empty array',
          {
            categories,
            type: typeof categories,
          }
        );
        return [];
      }

      return categories.map(transformBackendCategory);
    } catch (error) {
      handleApiError(error);
    }
  },
};
